import { forwardRef, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Center,
  Flex,
  Loader,
  Tabs,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { InfoCircle } from "iconsax-react";
import CardShopping from "./CardProduct";
import { convertDayWeek, convertToDate } from "../../../utils/date";
// import SearchDrawer from "../../../Layout/SubNavLink/Search";
// import FilterDrawer from "../../../Layout/SubNavLink/Filter";
import { IQueryMyPlans } from "../../../interfaces/IQuery";
import { useMyPlans } from "../../../hooks/useMyPlans";
import { EPlanStatus } from "../../../interfaces/MyPlans";
import { client_selected } from "../../../client";
import { useMediaQuery } from "@mantine/hooks";
import { Select } from "../../../components";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import i18n from "../../../utils/i18n";

interface IViews {
  total: number;
  approved: number;
  inProcessing: number;
  refused: number;
  cancelRequested: number;
  cancelled: number;
}
interface IContentTabs extends React.ComponentPropsWithoutRef<"div"> {
  id: string;
  label: string;
  badgeNumber: number;
  color: string;
  value: EPlanStatus | "";
  disabled: boolean;
}

const MyPurchases = () => {
  const isLargerThan991 = useMediaQuery("(min-width: 991px)");
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const languageSelected = i18n.language;

  const queryClient = useQueryClient();

  const isSpider = client_selected.name !== "Empiricus";
  const [activeTab, setActiveTab] = useState<EPlanStatus | null>(
    EPlanStatus.Approved
  );

  const [query, setQuery] = useState<IQueryMyPlans>({
    status: EPlanStatus.Approved,
  });

  const [views, setViews] = useState<IViews>({
    total: 0,
    approved: 0,
    inProcessing: 0,
    refused: 0,
    cancelRequested: 0,
    cancelled: 0,
  });

  const { total, approved, inProcessing, refused, cancelRequested, cancelled } =
    views;

  const { data, isLoading, isFetching } = useMyPlans(query);

  useEffect(() => {
    const language = localStorage.getItem("i18nextLng");
    if (languageSelected !== language)
      queryClient.invalidateQueries({
        queryKey: ["MyPlans", query.status],
        exact: false,
      });
  }, [languageSelected]);

  useEffect(() => {
    if (!!data?.views) {
      if (activeTab === EPlanStatus.Approved && data?.views.approved === 0) {
        setQuery((prev) => ({ ...prev, status: "" }));
        setActiveTab(EPlanStatus.All);
      }
      setViews(data.views);
    }
  }, [data, activeTab, approved]);

  const contentTabs: IContentTabs[] = [
    {
      id: "1",
      label: t("meu_portfolio.minhas_compras.content_tabs.all"),
      badgeNumber: total,
      color: "primary.1",
      value: EPlanStatus.All,
      disabled: total === 0,
    },
    {
      id: "2",
      label: t("meu_portfolio.minhas_compras.content_tabs.approved"),
      badgeNumber: approved,
      color: "alerts.2",
      value: EPlanStatus.Approved,
      disabled: approved === 0,
    },
    {
      id: "3",
      label: t("meu_portfolio.minhas_compras.content_tabs.in_processing"),
      badgeNumber: inProcessing,
      color: "alerts.1",
      value: EPlanStatus.InProcessing,
      disabled: inProcessing === 0,
    },
    {
      id: "4",
      label: t("meu_portfolio.minhas_compras.content_tabs.refused"),
      badgeNumber: refused,
      color: "alerts.0",
      value: EPlanStatus.Refused,
      disabled: refused === 0,
    },
    {
      id: "5",
      label: t("meu_portfolio.minhas_compras.content_tabs.cancel_requested"),
      badgeNumber: cancelRequested,
      color: "basic.4",
      value: EPlanStatus.CancelRequested,
      disabled: cancelRequested === 0,
    },
    {
      id: "6",
      label: t("meu_portfolio.minhas_compras.content_tabs.cancelled"),
      badgeNumber: cancelled,
      color: "basic.4",
      value: EPlanStatus.Cancelled,
      disabled: cancelled === 0,
    },
  ];

  const handleTabChange = (status: EPlanStatus | "") => {
    setQuery((prev) => ({
      ...prev,
      status: EPlanStatus.All === status ? "" : status,
    }));
  };

  const badgeNumber = contentTabs.find(
    (tab) => tab.value === query.status
  )?.badgeNumber;

  const SelectItem = forwardRef<HTMLDivElement, IContentTabs>(
    (
      { label, value, badgeNumber, color, disabled, ...others }: IContentTabs,
      ref
    ) => {
      return (
        <div
          ref={ref}
          {...others}
          style={{
            filter: disabled ? "opacity(0.5)" : "unset",
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          <Flex key={value} gap="8px">
            <Badge
              color={color}
              h={22}
              styles={{
                root: {
                  fontWeight: 600,
                  fontSize: "14px",
                },
              }}
              variant="filled"
              px={8}
            >
              {badgeNumber >= 10 ? badgeNumber : "0" + badgeNumber}
            </Badge>
            <Text
              c="basic.4"
              fw={{ base: 600 }}
              fz={{ base: 16 }}
              lh={{ base: "24.8px" }}
            >
              {label}
            </Text>
          </Flex>
        </div>
      );
    }
  );

  return (
    <Center bg="other.0" px={{ base: 24, 768: 60 }}>
      <Flex direction="column" w="100%" maw={1680} pb={96}>
        <Flex justify={"space-between"} align="center" mt={32} mb={20}>
          <Text
            fz={{ base: 32 }}
            fw={{ base: 700 }}
            c="basic.5"
            lh={{ base: "155%" }}
          >
            {t("meu_portfolio.minhas_compras.title")}
          </Text>
          {/* <Flex
            gap={16}
            sx={{
              button: {
                color: theme.colors.basic[5],
              },
            }}
          >
            <SearchDrawer />
            <FilterDrawer />
          </Flex> */}
        </Flex>
        {isLargerThan991 ? (
          <Tabs
            value={activeTab}
            onTabChange={(value) => {
              return setActiveTab(value as EPlanStatus);
            }}
            radius="xs"
            defaultValue={contentTabs[1].value}
            styles={{
              root: {
                zIndex: 1,
                overflowY: isLargerThan991 ? "auto" : "hidden",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "::-webkit-scrollbar": {
                  display: "none",
                },
              },
              tabsList: {
                borderBottom: `1px solid ${theme.colors.basic[2]}`,
                flexWrap: isLargerThan991 ? "wrap" : "nowrap",
              },
            }}
          >
            <Tabs.List>
              {contentTabs.map((tabItem, index) => {
                return isSpider ? (
                  <Tabs.Tab
                    key={`${index}-${tabItem?.label}`}
                    value={tabItem?.value}
                    onClick={() => handleTabChange(tabItem.value)}
                    disabled={tabItem?.badgeNumber === 0}
                    rightSection={
                      <Badge
                        color={tabItem?.color}
                        h={22}
                        styles={{
                          root: {
                            fontWeight: 600,
                            fontSize: "14px",
                          },
                        }}
                        variant="filled"
                        px={8}
                      >
                        {tabItem?.badgeNumber >= 10
                          ? tabItem?.badgeNumber
                          : "0" + tabItem?.badgeNumber}
                      </Badge>
                    }
                  >
                    {tabItem?.label}
                  </Tabs.Tab>
                ) : tabItem.value !== EPlanStatus.CancelRequested ? (
                  <Tabs.Tab
                    key={`${index}-${tabItem?.label}`}
                    value={tabItem?.value}
                    onClick={() => handleTabChange(tabItem.value)}
                    disabled={tabItem?.badgeNumber === 0}
                    rightSection={
                      <Badge
                        color={tabItem?.color}
                        h={22}
                        styles={{
                          root: {
                            fontWeight: 600,
                            fontSize: "14px",
                          },
                        }}
                        variant="filled"
                        px={8}
                      >
                        {tabItem?.badgeNumber >= 10
                          ? tabItem?.badgeNumber
                          : "0" + tabItem?.badgeNumber}
                      </Badge>
                    }
                  >
                    {tabItem?.label}
                  </Tabs.Tab>
                ) : null;
              })}
            </Tabs.List>
          </Tabs>
        ) : (
          <Select
            label={t("meu_portfolio.minhas_compras.select.label")}
            placeholder={t("meu_portfolio.minhas_compras.select.label")}
            data={contentTabs}
            itemComponent={SelectItem}
            value={query.status}
            id="select"
            styles={{
              icon: {
                width: "unset",
              },
              input: {
                paddingLeft: "64px !important",
              },
            }}
            icon={
              <Badge
                color={
                  contentTabs.find((tab) => tab.value === query.status)?.color
                }
                h={22}
                styles={{
                  root: {
                    fontWeight: 600,
                    fontSize: "14px",
                  },
                }}
                variant="filled"
                px={8}
              >
                {badgeNumber && badgeNumber >= 10
                  ? badgeNumber
                  : "0" + badgeNumber}
              </Badge>
            }
            onChange={(value) => {
              const newValue = value as EPlanStatus | "";
              handleTabChange(newValue);
            }}
          />
        )}
        {isLoading ? (
          <Center mt="32px">
            <Loader />
          </Center>
        ) : !!data?.dates && data?.dates?.length > 0 ? (
          data?.dates.map((user_plan, index) => {
            return (
              <Box key={`${index}-${user_plan?.date}`}>
                <Flex
                  align={"center"}
                  gap="8px"
                  justify={"start"}
                  mt={46}
                  mb={32}
                >
                  <Text
                    c="basic.4"
                    fw={{ base: 600 }}
                    fz={{ base: 16 }}
                    lh={{ base: "24.8px" }}
                  >
                    {convertToDate(user_plan?.date)}
                    <Text ml={6} component="span" fw={{ base: 400 }}>
                      ({convertDayWeek(user_plan?.date)}){" "}
                    </Text>
                  </Text>
                  <Tooltip
                    label={t("meu_portfolio.minhas_compras.tooltip_buy")}
                    withArrow
                    transitionProps={{ duration: 200 }}
                  >
                    <InfoCircle size={14} color={theme.colors.basic[5]} />
                  </Tooltip>
                </Flex>
                <Flex
                  direction="column"
                  w={"100%"}
                  gap={16}
                  bg="#fff"
                  p={user_plan.user_plans.length > 1 ? 32 : 0}
                  sx={{
                    boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.05)",
                    borderRadius: "12px",
                  }}
                >
                  {user_plan.user_plans.map((product, indexProduct) => {
                    return (
                      <CardShopping
                        key={`${indexProduct}-${product?.id}`}
                        product={product}
                      />
                    );
                  })}
                </Flex>
              </Box>
            );
          })
        ) : (
          <Center mt="32px">
            <Text
              fz={{ base: 26 }}
              fw={{ base: 700 }}
              c="basic.5"
              lh={{ base: "155%" }}
            >
              {t("global.pagination.no_data")}
            </Text>
          </Center>
        )}
      </Flex>
    </Center>
  );
};

export default MyPurchases;
